/* Analytics for the console (2026-10-28): routes, drivers and incidents.
   The data and its rules are the classic BusAnalyticsPage's — which profiles
   count as routes, when a route is learned, how a run is matched to a route,
   how the trail is drawn — moved here so the console's own pages can show
   them in the console's design. Collections: busRouteProfiles and busRoads
   (written by the route engine), driverRatings, busAttendance, busIssueReports. */
import { useEffect, useMemo, useState } from 'react';
import {
  collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp, Timestamp, updateDoc, where, writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { openLatLng } from '../../utils/locationCrypto';
import { loadTripTrailRaw } from '../../utils/tripTrail';
import { RUN_LITE_FIELDS, runLiteFromData, RunLite, RunSide } from '../../utils/routeWindow';
import { selectOrFull } from './firestoreRest';
import type { FleetModel, IncidentReport } from './model';

export type LatLng = { lat: number; lng: number };

export interface RouteProfile {
  id: string; busId: string; routeHash?: string; routeType?: string; startLabel: string; endLabel: string;
  startLat?: number; startLng?: number; endLat?: number; endLng?: number;
  avgDurationMinutes: number; avgDistanceKm?: number; tripCount: number; effectivenessScore: number;
  flags: string[]; flagReasons?: string[]; isLearned: boolean;
  lastSeen?: Timestamp | number | null; scoredAt?: Timestamp | number | null;
  recentDurations?: number[]; driverDurations?: Record<string, number[]>;
}

/** One bus, one side: the roads it actually drove, compared (engine/route/roadSweep.js). */
export interface BusRoads {
  id: string; busId: string; side: string; evidence: 'none' | 'thin' | 'ok'; sentence: string;
  fastestRoadId: string | null; slowestRoadId: string | null; tripsConsidered: number;
  roads: { roadId: string; name?: string | null; trips: number; avgMinutes: number; avgStops: number; isLearned: boolean;
    crawlZones: { avgSeconds: number; hourLocal: number | null }[] }[];
}

export interface DriverRating {
  id: string; driverId: string; stars: number; safetyScore: number; fuelScore: number;
  complianceScore: number; coverageScore?: number; timeScore: number;
  totalTrips?: number; windowTrips?: number; activeIncidentFlags?: number;
}

export interface PinnedRun { id: string; busId: string; name: string; date: Date | null; type: string; km: number | null; minutes: number | null }

/** Trips before a route counts as learned — engine/route/reconcile.js LEARNED_AFTER_TRIPS. */
/** The route engine's flags (engine/route/score.js), in words — never the raw code. */
const FLAG_WORDS: Record<string, string> = {
  HIGH_INCIDENTS: 'Incidents on its trips',
  TIME_DRIFT: 'Taking longer than usual',
  WORSENING_TREND: 'Slower week after week',
  INCONSISTENT: 'Times jump around',
};
export const flagWords = (flags: string[] | undefined): string[] =>
  (flags || []).map((x) => FLAG_WORDS[x] || x.toLowerCase().replace(/_/g, ' '));

export const LEARNED_AFTER_TRIPS = 3;
/** Shorter than this is a desk test, not a route — BusService.kMinRouteLearnKm. */
export const MIN_ROUTE_LEARN_KM = 0.5;
const STALE_ROUTE_MS = 30 * 86400000;

const msOf = (v: Timestamp | number | null | undefined) => (typeof v === 'number' ? v : v && typeof v.toMillis === 'function' ? v.toMillis() : 0);

export type HealthTone = 'ok' | 'mute' | 'warn' | 'bad';
/** The route score in four words — the classic page's tiers. */
export function routeHealth(s: number): { tone: HealthTone; label: string; hint: string } {
  if (s >= 80) return { tone: 'ok', label: 'Healthy', hint: 'Running well — not losing time on this route.' };
  if (s >= 60) return { tone: 'mute', label: 'Alright', hint: 'Mostly fine. Keep an eye on it.' };
  if (s >= 40) return { tone: 'warn', label: 'Drifting', hint: 'Getting slower or less steady — worth a look soon.' };
  return { tone: 'bad', label: 'Concern', hint: 'Check this route and ask the driver.' };
}
/** Map colours for a tone; the map needs real colours, not the page's variables. */
export const toneHex = (tone: HealthTone, dark: boolean) => ({
  ok: dark ? '#4CC38A' : '#1C7A4B', mute: dark ? '#9AA5AF' : '#56606B', warn: dark ? '#E8B04A' : '#985A00', bad: dark ? '#F07272' : '#C23B3B',
}[tone]);

export function routeSide(p: RouteProfile): RunSide {
  const t = (p.routeType ?? '').toLowerCase();
  if (t.includes('school_trip')) return 'trip';
  if (t.includes('evening') || t.includes('return')) return 'evening';
  if (t.includes('morning') || t.includes('outbound')) return 'morning';
  return 'other';
}
export const SIDES: RunSide[] = ['morning', 'evening', 'trip', 'other'];
export const sideLabel = (s: RunSide) => (s === 'morning' ? 'Morning' : s === 'evening' ? 'Afternoon' : s === 'trip' ? 'School trips' : 'Other');

/** "Westlands → Hidden Leaf", or "Westlands · out and back" when both ends are the same place. */
export function routeName(p: RouteProfile): string {
  const start = (p.startLabel ?? '').trim();
  const end = (p.endLabel ?? '').trim();
  if (start && end && start.toLowerCase() === end.toLowerCase()) return `${start} · out and back`;
  return `${start || 'unnamed stop'} → ${end || 'unnamed stop'}`;
}

/** Hide desk-test junk (0 km · 3 min) — a legacy profile without km counts if its time looks real. */
export function isCredibleRoute(p: RouteProfile): boolean {
  const km = p.avgDistanceKm ?? 0;
  if (km >= MIN_ROUTE_LEARN_KM) return true;
  return km <= 0 && (p.avgDurationMinutes ?? 0) >= 5 && p.tripCount >= 1;
}

/** No one has driven it in ~30 days: its score is no longer news. */
export const isStaleRoute = (p: RouteProfile) => {
  const ms = msOf(p.scoredAt ?? p.lastSeen ?? null);
  return ms > 0 && Date.now() - ms > STALE_ROUTE_MS;
};

/** Precise endpoints if stored, else read back out of the routeHash geocells. */
export function routeCoords(p: RouteProfile): { start: LatLng; end: LatLng } | null {
  const ok = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
  if (ok(p.startLat) && ok(p.startLng) && ok(p.endLat) && ok(p.endLng)) {
    return { start: { lat: p.startLat, lng: p.startLng }, end: { lat: p.endLat, lng: p.endLng } };
  }
  if (p.routeHash) {
    const [s, e] = p.routeHash.split('__');
    const sc = (s ?? '').split('_').map(Number);
    const ec = (e ?? '').split('_').map(Number);
    if (sc.length === 2 && ec.length === 2 && sc.every(Number.isFinite) && ec.every(Number.isFinite)) {
      return { start: { lat: sc[0], lng: sc[1] }, end: { lat: ec[0], lng: ec[1] } };
    }
  }
  return null;
}

export const minutesText = (m: number | null | undefined) => {
  if (!m) return '—';
  const h = Math.floor(m / 60);
  const min = Math.round(m % 60);
  return h === 0 ? `${min} min` : `${h} h ${min} min`;
};
export const kmText = (km: number | null | undefined) => ((km ?? 0) > 0 ? `${(km as number).toFixed(1)} km` : '—');

/** Route profiles and road summaries, live. */
export function useRouteData(orgCode: string) {
  const [profiles, setProfiles] = useState<RouteProfile[] | null>(null);
  const [roads, setRoads] = useState<BusRoads[]>([]);
  useEffect(() => {
    if (!orgCode) return undefined;
    // A safety cap; no orderBy (it would need a composite index).
    const u1 = onSnapshot(query(collection(db, 'busRouteProfiles'), where('orgCode', '==', orgCode), limit(300)),
      (snap) => setProfiles(snap.docs.map((d) => ({ id: d.id, ...d.data() } as RouteProfile))), () => setProfiles([]));
    const u2 = onSnapshot(query(collection(db, 'busRoads'), where('orgCode', '==', orgCode), limit(100)),
      (snap) => setRoads(snap.docs.map((d) => ({ id: d.id, ...d.data() } as BusRoads))), () => setRoads([]));
    return () => { u1(); u2(); };
  }, [orgCode]);
  return { profiles, roads };
}

export type WindowKey = 'all' | 'd30' | 'term';

/** The runs a window counts. The last 30 days are already loaded with the
 *  fleet; a term that started earlier is read per bus from its start. */
export function useWindowRuns(f: FleetModel, key: WindowKey, termStart: Date | null) {
  const loaded = useMemo(() => f.runs.map((r) => r.lite).filter((r): r is RunLite => !!r), [f.runs]);
  const [fetched, setFetched] = useState<{ key: string; runs: RunLite[] } | null>(null);
  const oldest = useMemo(() => Date.now() - 30 * 86400000, []);
  const needFetch = key === 'term' && !!termStart && termStart.getTime() < oldest;
  const busKey = f.buses.map((b) => b.id).join(',');
  const fetchKey = `${f.org.code}|${busKey}|${termStart?.getTime() ?? ''}`;
  useEffect(() => {
    if (!needFetch || !termStart || !f.org.code) return undefined;
    let live = true;
    (async () => {
      const all: RunLite[] = [];
      for (const bid of busKey.split(',').filter(Boolean)) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const rows = await selectOrFull({
            collection: 'busAttendance', filters: [['schoolCode', '==', f.org.code], ['busId', '==', bid], ['routeStartedAt', '>=', termStart]],
            orderBy: { field: 'routeStartedAt', desc: true }, limit: 200, fields: RUN_LITE_FIELDS,
          });
          rows.forEach((d) => { const r = runLiteFromData(d.id, d.data); if (r) all.push(r); });
        } catch { /* that bus's runs stay out of the count */ }
      }
      if (live) setFetched({ key: fetchKey, runs: all });
    })();
    return () => { live = false; };
  }, [needFetch, termStart, f.org.code, busKey, fetchKey]);
  if (key === 'all') return { runs: [] as RunLite[], loading: false };
  if (!needFetch) return { runs: loaded, loading: false };
  return fetched?.key === fetchKey ? { runs: fetched.runs, loading: false } : { runs: [] as RunLite[], loading: true };
}

/** Runs the office named on the bus page ("the Tsavo trip"). One equality read per bus. */
export function usePinnedRuns(orgCode: string, busIds: string[]) {
  const [pinned, setPinned] = useState<PinnedRun[]>([]);
  const key = busIds.join(',');
  useEffect(() => {
    if (!orgCode || !key) { setPinned([]); return undefined; }
    let live = true;
    (async () => {
      const out: PinnedRun[] = [];
      for (const bid of key.split(',')) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const rows = await selectOrFull({
            collection: 'busAttendance', filters: [['schoolCode', '==', orgCode], ['busId', '==', bid], ['pinned', '==', true]],
            limit: 50, fields: ['pinnedName', 'routeStartedAt', 'date', 'routeType', 'distanceKm', 'durationMinutes'],
          });
          rows.forEach((d) => {
            const x = d.data;
            const ts = (v: unknown) => (v && typeof (v as Timestamp).toDate === 'function' ? (v as Timestamp).toDate() : null);
            const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
            out.push({
              id: d.id, busId: bid, name: typeof x.pinnedName === 'string' && x.pinnedName ? x.pinnedName : 'Pinned run',
              date: ts(x.routeStartedAt) ?? ts(x.date), type: typeof x.routeType === 'string' ? x.routeType : '', km: num(x.distanceKm), minutes: num(x.durationMinutes),
            });
          });
        } catch { /* that bus's pins stay out */ }
      }
      out.sort((a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0));
      if (live) setPinned(out);
    })();
    return () => { live = false; };
  }, [orgCode, key]);
  return pinned;
}

export type TrailPoint = LatLng & { gapBefore?: boolean };

/** One trip's drawn line: its road-snapped points, else its raw trail (sealed
 *  points opened, spilled chunks included). Empty with fewer than two points. */
export async function trailOfRun(id: string, data: Record<string, unknown>): Promise<TrailPoint[]> {
  const snapped = (Array.isArray(data.routePointsSnapped) ? (data.routePointsSnapped as Record<string, unknown>[]) : [])
    .map((x) => ({ lat: Number(x.lat), lng: Number(x.lng), gapBefore: x.gapBefore === true }))
    .filter((x) => Number.isFinite(x.lat) && Number.isFinite(x.lng));
  if (snapped.length >= 2) return snapped;
  const raw = Array.isArray(data.routePoints) ? (data.routePoints as unknown[]) : [];
  const whole = Number(data.trailChunks ?? 0) > 0
    ? await loadTripTrailRaw(db, 'busAttendance', id, data as { routePoints?: unknown[]; trailChunks?: number; trailWindowStart?: number | null })
    : raw;
  const opened = await Promise.all(whole.map((pt) => openLatLng(pt)));
  const path: TrailPoint[] = [];
  opened.forEach((x, i) => {
    if (!x) return;
    const r = whole[i] as Record<string, unknown> | null;
    path.push({ lat: x.lat, lng: x.lng, gapBefore: !!r && r.gapBefore === true });
  });
  return path.length >= 2 ? path : [];
}

/** One whole trip — the record with its trail — for the trip dialog. Null when it is gone. */
export async function loadRun(id: string): Promise<{ data: Record<string, unknown>; path: TrailPoint[] } | null> {
  const snap = await getDoc(doc(db, 'busAttendance', id));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, unknown>;
  return { data, path: await trailOfRun(id, data) };
}

/** Name a trip so it is listed under Analytics → Pinned trips, or take the name off. */
export async function pinRun(id: string, name: string | null, by: string | null): Promise<void> {
  await updateDoc(doc(db, 'busAttendance', id), name
    ? { pinned: true, pinnedName: name, pinnedAt: serverTimestamp(), pinnedBy: by }
    : { pinned: false, pinnedName: null, pinnedAt: null });
}

/** The drawn line of a route: the densest recent completed trip of that bus and
 *  route type — road-snapped points first, else the raw trail (sealed points
 *  opened, chunks included). Empty when no trip has a usable trail. */
export function useRouteTrail(p: RouteProfile | null) {
  const [trail, setTrail] = useState<{ forId: string; path: (LatLng & { gapBefore?: boolean })[] } | null>(null);
  useEffect(() => {
    if (!p) { setTrail(null); return undefined; }
    let live = true;
    const forId = p.id;
    (async () => {
      try {
        const rt = (p.routeType ?? '').trim().toLowerCase();
        const snap = await getDocs(query(collection(db, 'busAttendance'), where('busId', '==', p.busId), orderBy('routeStartedAt', 'desc'), limit(24)));
        const size = (x: Record<string, unknown>) => [
          Array.isArray(x.routePointsSnapped) ? x.routePointsSnapped.length : 0,
          (Array.isArray(x.routePoints) ? x.routePoints.length : 0) + Number(x.trailSpilled ?? 0),
        ];
        const docs = [...snap.docs].sort((a, b) => {
          const [sa, ra] = size(a.data()); const [sb, rb] = size(b.data());
          return (sb - sa) || (rb - ra);
        });
        for (const d of docs) {
          const data = d.data() as Record<string, unknown>;
          const dataRt = String(data.routeType ?? '').trim().toLowerCase();
          if (rt && dataRt && dataRt !== rt) continue;
          const raw = Array.isArray(data.routePoints) ? (data.routePoints as unknown[]) : [];
          if (raw.length < 2) continue;
          const km = Number(data.distanceKm ?? 0);
          if (km > 0 && km < MIN_ROUTE_LEARN_KM) continue;
          // eslint-disable-next-line no-await-in-loop
          const path = await trailOfRun(d.id, data);
          if (path.length >= 2) { if (live) setTrail({ forId, path }); return; }
        }
        if (live) setTrail({ forId, path: [] });
      } catch {
        if (live) setTrail({ forId, path: [] });
      }
    })();
    return () => { live = false; };
  }, [p]);
  return trail && p && trail.forId === p.id ? trail.path : null;
}

/** Driver ratings, live. Trip-conductor logins (tripc_…) are crew for one trip, not drivers. */
export function useDriverRatings(orgCode: string) {
  const [ratings, setRatings] = useState<DriverRating[] | null>(null);
  useEffect(() => {
    if (!orgCode) return undefined;
    return onSnapshot(query(collection(db, 'driverRatings'), where('orgCode', '==', orgCode), limit(300)),
      (snap) => setRatings(snap.docs.map((d) => ({ id: d.id, ...d.data() } as DriverRating))
        .filter((r) => !String(r.driverId ?? r.id).startsWith('tripc_'))),
      () => setRatings([]));
  }, [orgCode]);
  return ratings;
}


/** Close incident reports, and their own 'incident' alerts on the same bus
 *  within 15 minutes of each report, so the notification goes with it. */
export async function resolveIncidents(f: FleetModel, reports: IncidentReport[], uid: string | null): Promise<void> {
  if (!reports.length) return;
  const near = 15 * 60 * 1000;
  const alertIds = new Set<string>();
  reports.forEach((r) => f.alerts.forEach((a) => {
    if (a.read || a.kind !== 'incident' || a.busId !== r.busId) return;
    if (!r.at || !a.at || Math.abs(a.at.getTime() - r.at.getTime()) <= near) alertIds.add(a.id);
  }));
  for (let i = 0; i < reports.length; i += 400) {
    const batch = writeBatch(db);
    reports.slice(i, i + 400).forEach((r) => batch.update(doc(db, 'busIssueReports', r.id), {
      resolved: true, resolvedAt: serverTimestamp(), resolvedBy: uid, resolvedNote: 'Closed in Analytics',
    }));
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }
  const ids = [...alertIds];
  for (let i = 0; i < ids.length; i += 400) {
    const batch = writeBatch(db);
    ids.slice(i, i + 400).forEach((id) => batch.update(doc(db, 'fleetAlerts', id), { read: true }));
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }
}

/** Open incident reports — the count beside Analytics. */
export const openIncidents = (f: FleetModel) => (f.incidents || []).filter((r) => !r.resolved);
